import { Container, Row, Col, Spinner, Form } from "react-bootstrap";
import { useState, useEffect } from "react";
import CustomBreadcrumbs from "../../navigation/breadcrumbs/CustomBreadcrumbs";
import KPICard from "../../dashboard/card/KPICard";
import { getTimePassed } from "../../../utils/TimePassed";
import { createKPIArrayFromSessionInfos } from "../../../utils/KPIExtractor";
import ScalarValuesContainer from "./ScalarValuesContainer";
import MapCard from "../../dashboard/card/MapCard";
import GraphsContainer from "./GraphsContainer";
import UploadFITFileButton from "./UploadFITFileButton";
import DownloadReportButton from "./DownloadReportButton";
import { requestWithAuthorization } from "../../../network/apiRequests";
import { useSessionData } from "../../../sessions/useSessionData";
import { useNavigate } from "react-router-dom";
import DashboardButton from "../../dashboard/DashboardButton";
import { MdOutlineAnalytics, MdDownload } from "react-icons/md";
import DownloadDataModal from "./DownloadDataModal";

interface SessionDetailComponentProps {
  sessionId: string;
  sensorId: string;
}

export default function SessionDetailComponent({
  sessionId,
  sensorId: initialSensorId,
}: SessionDetailComponentProps) {
  const [selectedSensorId, setSelectedSensorId] = useState<string>(initialSensorId);
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [sensorIds, setSensorIds] = useState<string[]>([]);
  const navigate = useNavigate();

  // Sync state if initial sensorId changes
  useEffect(() => {
    if (initialSensorId) {
      setSelectedSensorId(initialSensorId);
    }
  }, [initialSensorId]);

  const { sessionDetails, hasThirdPartyData, isLoading, error } = useSessionData({
    sessionId,
    sensorId: selectedSensorId
  });

  // Fetch user license/plan
  useEffect(() => {
    const fetchLicense = async () => {
      try {
        const response = await requestWithAuthorization(
          `${process.env.REACT_APP_API_HOSTNAME}/user/license`
        );
        if (response.status === 200) {
          setUserPlan(response.data?.plan_id ?? "free");
        }
      } catch (err) {
        console.error("Failed to fetch user license:", err);
        setUserPlan("free");
      }
    };

    fetchLicense();
  }, []);

  // Fetch all sensor IDs for this recording
  useEffect(() => {
    if (!sessionId) return;

    const fetchSensorIds = async () => {
      try {
        const response = await requestWithAuthorization(
          `${process.env.REACT_APP_API_HOSTNAME}/recordings/${sessionId}`
        );
        if (response.status === 200) {
          let ids: string[] = [];
          const data = response.data;
          if (Array.isArray(data?.sensorId)) {
            ids = data.sensorId;
          } else if (typeof data?.sensorId === 'string') {
            ids = [data.sensorId];
          } else if (Array.isArray(data?.sensorIds)) {
            ids = data.sensorIds;
          }
          setSensorIds(ids);

          // If current selected sensor is not in list (or list is loaded), ensure a valid selection
          if (ids.length > 0 && !ids.includes(selectedSensorId)) {
            setSelectedSensorId(ids[0]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch recording sensor IDs:", err);
      }
    };

    fetchSensorIds();
  }, [sessionId]);

  const handleSensorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSensorId = e.target.value;
    setSelectedSensorId(newSensorId);
    navigate(`/sessions/${sessionId}?sensorId=${newSensorId}`, { replace: true });
  };

  const handleAnalysisClick = () => {
    navigate("/analysis", {
      state: {
        recordingId: sessionId,
        name: sessionDetails?.name,
        startDateTime: sessionDetails?.startDateTime,
        endDateTime: sessionDetails?.endDateTime,
      },
    });
  };

  let containerContent = <></>;
  if (isLoading || (!sessionDetails && !error)) {
    containerContent = (
      <Row>
        <Col className="d-flex justify-content-center">
          <Spinner animation="border" variant="light" />
        </Col>
      </Row>
    );
  } else if (error) {
    containerContent = (
      <Row>
        <Col className="d-flex justify-content-center">
          <h1>Error loading session: {error}</h1>
        </Col>
      </Row>
    );
  } else if (!sessionDetails) {
    containerContent = (
      <Row>
        <Col className="d-flex justify-content-center">
          <h1>Session not found</h1>
        </Col>
      </Row>
    );
  } else {
    const timePassedString = getTimePassed(
      sessionDetails?.startDateTime || ''
    );
    const KPIs = createKPIArrayFromSessionInfos(sessionDetails?.infos);
    containerContent = (
      <>
        <Row>
          <Col>
            <Row className="align-items-center mb-2">
              <Col md={6} className="d-flex align-items-center gap-3">
                <h1 className="mb-0">{`${sessionDetails?.name || 'Session'}`}</h1>

                {/* Sensor selection dropdown if multiple sensors exist */}
                {sensorIds.length > 1 && (
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small fw-bold text-uppercase me-1" style={{ whiteSpace: 'nowrap' }}>
                      Sensor:
                    </span>
                    <Form.Select
                      size="sm"
                      value={selectedSensorId}
                      onChange={handleSensorChange}
                      style={{
                        backgroundColor: '#1f2937',
                        color: '#fff',
                        borderColor: '#495057',
                        width: 'auto',
                        minWidth: '180px',
                        cursor: 'pointer'
                      }}
                      id="sensor-select-dropdown"
                    >
                      {sensorIds.map((id) => (
                        <option key={id} value={id}>
                          {id}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                )}
              </Col>
              <Col md={6} className="d-flex justify-content-end gap-2">
                {userPlan !== "free" && <UploadFITFileButton sessionId={sessionId} hasThirdPartyData={hasThirdPartyData} />}
                {userPlan === "admin" && <DownloadReportButton sessionId={sessionId} />}
                <DashboardButton
                  icon={<MdDownload />}
                  text="Download Data"
                  onClick={() => setShowDownloadModal(true)}
                />
                <DashboardButton
                  icon={<MdOutlineAnalytics />}
                  text="Analysis"
                  onClick={handleAnalysisClick}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <span className="detail-page-subheading">{`${sessionDetails?.startDateTime ? new Date(sessionDetails.startDateTime).toLocaleDateString() : ''} - ${sessionDetails?.startDateTime ? new Date(sessionDetails.startDateTime).toLocaleTimeString() : ''}`}</span>
              </Col>
            </Row>
          </Col>
        </Row>
        <ScalarValuesContainer
          timePassedString={timePassedString}
          values={sessionDetails?.infos}
        />
        <Row className="pt-1">
          <Col xl={8} className="pe-1">
            {/* Left side */}
            <Row>
              <Col>
                <KPICard
                  sessionID={sessionId}
                  sensorID={selectedSensorId}
                  heading={sessionDetails?.name || 'Session'}
                  subheading={sessionDetails?.endDateTime ? new Date(sessionDetails.endDateTime).toLocaleDateString() : ''}
                  kpis={KPIs}
                />
              </Col>
            </Row>
          </Col>
          <Col xl={4} className="ps-1">
            {/* Right side */}
            <Row className="pb-2">
              <Col>
                <MapCard sessionID={sessionId} sensorID={selectedSensorId} showButton={true} />
              </Col>
            </Row>
          </Col>
        </Row>
        <GraphsContainer sessionId={sessionId} sensorId={selectedSensorId} />
      </>
    );
  }

  return (
    <Container fluid className="background-color text-white p-4">
      <Row>
        <Col>
          <CustomBreadcrumbs
            path={[
              { path: "/dashboard", title: "Dashboard" },
              { path: "/sessions", title: "Sessions" },
            ]}
            page={`${sessionDetails?.name || "Detail"}`}
          />
        </Col>
      </Row>
      <Container fluid className="pt-4 pb-2 px-1">
        {containerContent}
      </Container>

      {sessionDetails && (
        <DownloadDataModal
          show={showDownloadModal}
          onHide={() => setShowDownloadModal(false)}
          recordingId={sessionId}
          recordingName={sessionDetails.name || "Recording"}
          sensorIds={sensorIds}
          startTime={sessionDetails.startDateTime || ""}
          endTime={sessionDetails.endDateTime || ""}
          hasThirdPartyData={hasThirdPartyData}
        />
      )}
    </Container>
  );
}
